import { ReactNode, useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useDebounceValue } from 'usehooks-ts';
import { ColumnDef, SortingState } from '@tanstack/react-table';
import Skeleton from '@mui/material/Skeleton';

import { ManagementSupportRequestDtoIn, EligibleAmountItemDtoIn, SupportRequestsQueryParams, EligibleAmountsQueryDtoIn } from '@/models/in/ManagementSupportRequestDtoIn';
import { RelatedSupportRequestDtoIn } from '@/models/in/RelatedSupportRequestDtoIn';
import { DocumentWithDetailsDtoIn } from '@/models/in/DocumentWithDetailsDtoIn';
import SupportRequestType from '@/models/common/SupportRequestType';
import AllowedFor from '@/models/common/AllowedFor';
import { FundExpenseDetails } from '@/models/FundExpenseDetails';
import { getSupplierDisplayDetails } from '@/helpers/supplierDisplayHelper';
import { getShortRequestId } from '@/helpers/requestIdHelper';
import { downloadFileFromApi } from '@/helpers/downloadFile';
import DataTable from '@/components/ui/DataTable/DataTable';
import SelectAllHeader from '@/components/ui/DataTable/SelectAllHeader';
import cellType from '@/components/ui/DataTable/CellTypes';
import { Cell, Col } from '@/components/ui/DataTable/ColumnsHelpers';
import tableStyles from '@/components/ui/DataTable/DataTable.module.scss';
import { useGetAuthApi, usePostAuthApi, usePostAuthApiInfiniteQuery, useDeleteAuthApi } from '@/hooks/UseApiCalls';
import { queryClient } from '@/hooks/QueryClient';
import { queryKeys } from '@/hooks/QueryKeys';
import { getAuth } from '@/api/ApiCalls';
import useAuthContext from '@/contexts/Auth/useAuthContext';
import { ManagementTabType } from '@/models/common/ManagementTabType';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/Tabs/Tabs';
import AdditionalInfo from './AdditionalInfo/AdditionalInfo';
import FiltersArea from './FiltersArea';
import ApprovedAmountDialog from './ApprovedAmountDialog';
import StatusChangeDialog from './StatusChangeDialog/StatusChangeDialog';
import ChangeBankAccountDialog from './ChangeBankAccountDialog';
import BulkStatusChangeDialog from './BulkStatusChangeDialog/BulkStatusChangeDialog';
import OfficeCommentsDialog from './OfficeCommentsDialog/OfficeCommentsDialog';
import EditRequestedForDateDialog from './EditRequestedForDateDialog/EditRequestedForDateDialog';
import ChangeGoalTypeDialog from './ChangeGoalTypeDialog/ChangeGoalTypeDialog';
import ConvertToFundExpenseDialog from './ConvertToFundExpenseDialog/ConvertToFundExpenseDialog';
import RequestCompletionDialog from '@/components/RequestCompletionDialog/RequestCompletionDialog';
import { exportSupportRequestsToCsv } from '@/helpers/exportSupportRequestsToCsv';
import { buildAvailableBalancePendingTooltipText } from '@/helpers/availableBalanceTooltipText';
import { translateGoalSearchTerm } from '@/helpers/goalSearchHelper';
import useTableDensity from '@/hooks/useTableDensity';
import editIcon from '@/assets/icons/edit.svg';
import bankIcon from '@/assets/icons/bank.svg';
import { PaymentMethodType } from '@/models/common/PaymentMethodType';
import useReturnedPaymentFeature from '@/hooks/useReturnedPaymentFeature';
import infoIcon from '@/assets/icons/info2.svg';
import tooltipQuestion from '@/assets/icons/tooltip-question.svg';
import CustomTooltip from '@/components/ui/Tooltip/Tooltip';

// A receipt (אסמכתא) only exists for one-time requests that actually reached Done - "אושרה"/
// InitialApprovalApproved isn't enough, the payment may still be pending execution. Monthly
// requests are out of scope entirely (each row there is one of several separate transfers, no
// single execution date/amount to put on a single receipt).
const isReceiptEligible = (row: ManagementSupportRequestDtoIn): boolean =>
  row.type === SupportRequestType.OneTime && String(row.status) === 'Done';

// Helper function to get common allowed statuses across selected requests
const getCommonAllowedStatuses = (selectedRequests: ManagementSupportRequestDtoIn[]): string[] => {
  if (selectedRequests.length === 0) return [];

  const statusSets = selectedRequests.map((r) => new Set(r.allowedNextStatuses || []));

  // Find intersection of all status sets
  if (statusSets.length === 0 || statusSets[0].size === 0) return [];

  return [...statusSets[0]].filter((status) =>
    statusSets.every((set) => set.has(status))
  );
};

// Constants
/**
 * HAT-1395: the status select's extra entry on a month already «תשלום חזר» whose account was kept -
 * marked before this flow, or with its toggle off. Not a status: it opens the status window with
 * the same status, to delete the account and ask the coordinator for another.
 */
const ASK_NEW_ACCOUNT = '__askNewAccount';

/** Statuses, of either lifecycle, after which a month no longer waits for anything - the server's IsLive. */
const CLOSED_STATUSES = ['Done', 'DonePendingCompletion', 'DoneCompletionReturned', 'Rejected', 'Cancelled'];

const SEARCH_DEBOUNCE_MS = 300;
const DENSITY_STORAGE_KEY = 'table.density.supportRequestsManagement';

// Map column IDs to backend sort field names
const SORT_FIELD_MAP: Record<string, string> = {
  dateColumn: 'RequestedAt',
  fundId: 'FundId',
  supportedName: 'SupportedName',
  requestedAmount: 'RequestedAmount',
  approvedAmount: 'ApprovedAmount',
  goalName: 'GoalName',
};

const INITIAL_STATUS_TO_TAB: Record<string, ManagementTabType> = {
  PendingFirstReview: ManagementTabType.PendingApproval,
  PendingManagementApproval: ManagementTabType.PendingApproval,
  PendingExceptionsCommitteeApproval: ManagementTabType.PendingExceptionsCommitteeApproval,
  PendingCompletion: ManagementTabType.CompletionReturned,
  CompletionReturned: ManagementTabType.CompletionReturned,
  Rejected: ManagementTabType.Archive,
  Cancelled: ManagementTabType.Archive,
};

const STATUS_TO_TAB: Record<string, ManagementTabType> = {
  PendingExceptionsCommitteeApproval: ManagementTabType.PendingExceptionsCommitteeApproval,
  PendingCompletion: ManagementTabType.CompletionReturned,
  CompletionReturned: ManagementTabType.CompletionReturned,
  DonePendingCompletion: ManagementTabType.CompletionReturned,
  DoneCompletionReturned: ManagementTabType.CompletionReturned,
  InitialApprovalApproved: ManagementTabType.Approved,
  PendingExecution: ManagementTabType.PendingExecution,
  Done: ManagementTabType.Archive,
  Rejected: ManagementTabType.Archive,
  Cancelled: ManagementTabType.Archive,
  PaymentReturned: ManagementTabType.Archive,
};

const getTabForRequest = (item: ManagementSupportRequestDtoIn): ManagementTabType => {
  if (item.isInInitialStatusProcess) {
    return INITIAL_STATUS_TO_TAB[String(item.initialStatus)] ?? ManagementTabType.PendingApproval;
  }
  return STATUS_TO_TAB[String(item.status)] ?? ManagementTabType.PendingApproval;
};

const SupportRequestsManagementPage = () => {
  const { t, i18n } = useTranslation();
  const isReturnedPaymentOn = useReturnedPaymentFeature();
  const navigate = useNavigate();
  const { currentUser } = useAuthContext();
  const [selectedTab, setSelectedTab] = useState<ManagementTabType>(ManagementTabType.PendingApproval);
  const [selectedRequest, setSelectedRequest] = useState<ManagementSupportRequestDtoIn>();
  const [documents, setDocuments] = useState<DocumentWithDetailsDtoIn[]>([]);
  const [isLoadingDocs, setIsLoadingDocs] = useState(false);

  // Page size state
  const [pageSize, setPageSize] = useState<number>(50);

  // Search state with debounce
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch] = useDebounceValue(searchQuery, SEARCH_DEBOUNCE_MS);
  const [density, setDensity] = useTableDensity(DENSITY_STORAGE_KEY, 'normal');

  // Filter state (lifted from FiltersArea for server-side filtering)
  const [selectedTypes, setSelectedTypes] = useState<SupportRequestType[]>([]);
  // HAT-1259: undefined = every request, true = third-party only, false = ordinary only.
  const [isThirdPartyPaymentFilter, setIsThirdPartyPaymentFilter] = useState<boolean | undefined>(undefined);
  const [dateRange, setDateRange] = useState<{ from: string; to: string } | null>(null);
  const [selectedFundId, setSelectedFundId] = useState<number | null>(null);
  const [selectedBeneficiaryContactId, setSelectedBeneficiaryContactId] = useState<number | null>(null);
  const [selectedSupplierId, setSelectedSupplierId] = useState<number | null>(null);

  // Sorting state - for display (what the user sees in the table)
  const [sorting, setSorting] = useState<SortingState>([{ id: 'dateColumn', desc: true }]);
  // Server sorting state - tracks the last server-side sort (used for queryParams)
  const [serverSort, setServerSort] = useState<{ id: string; desc: boolean }>({ id: 'dateColumn', desc: true });

  // Approved amount dialog state
  const [approvedAmountDialogOpen, setApprovedAmountDialogOpen] = useState(false);
  const [requestForAmountEdit, setRequestForAmountEdit] = useState<ManagementSupportRequestDtoIn | null>(null);

  // Status change dialog state
  const [statusChangeDialogOpen, setStatusChangeDialogOpen] = useState(false);
  const [pendingStatusChange, setPendingStatusChange] = useState<{
    correlationId: string;
    counter: number | null;
    isInInitialStatusProcess: boolean;
    newStatus: string;
    // Snapshot of the request being transitioned so the StatusChangeDialog can
    // run the supplier approval gate (withholding notice + missing-approval
    // confirmation) without re-fetching.
    request: ManagementSupportRequestDtoIn | null;
  } | null>(null);

  // Change bank account dialog state
  const [changeBankAccountDialogOpen, setChangeBankAccountDialogOpen] = useState(false);
  const [requestForBankAccountEdit, setRequestForBankAccountEdit] = useState<ManagementSupportRequestDtoIn | null>(null);

  // Bulk status change dialog state
  const [bulkStatusDialogOpen, setBulkStatusDialogOpen] = useState(false);
  const [selectedForBulkUpdate, setSelectedForBulkUpdate] = useState<ManagementSupportRequestDtoIn[]>([]);
  const [commonStatuses, setCommonStatuses] = useState<string[]>([]);

  // Office comments dialog state
  const [officeCommentsDialogOpen, setOfficeCommentsDialogOpen] = useState(false);

  // Related support requests (for exceptions committee blade)
  const relatedSupportRequestsUrl = selectedRequest && selectedTab === ManagementTabType.PendingExceptionsCommitteeApproval
    ? `management/support-requests/${selectedRequest.multiRequestCorrelationId}/related-support-requests${selectedRequest.multiRequestCounter != null ? `?counter=${selectedRequest.multiRequestCounter}` : ''}`
    : '';
  const { data: relatedSupportRequests, isLoading: isLoadingRelatedSupportRequests } = useGetAuthApi<RelatedSupportRequestDtoIn[]>(relatedSupportRequestsUrl);

  // Edit requested-for-date dialog state
  const [editRequestedForDateDialogOpen, setEditRequestedForDateDialogOpen] = useState(false);

  // Request completion dialog state
  const [requestCompletionDialogOpen, setRequestCompletionDialogOpen] = useState(false);
  const [isDoneWithPendingCompletion, setIsDoneWithPendingCompletion] = useState(false);

  // Change goal-type dialog state. We snapshot the request when the dialog opens so the dialog
  // can keep showing its post-success message (e.g. "moved to exceptions committee") even after
  // the request has migrated to a different management tab and the blade's selectedRequest has
  // been cleared.
  const [requestForGoalTypeChange, setRequestForGoalTypeChange] = useState<ManagementSupportRequestDtoIn | null>(null);

  // Convert-to-fund-expense dialog state (same snapshot pattern as the goal-type dialog).
  const [requestForConvertToFundExpense, setRequestForConvertToFundExpense] = useState<ManagementSupportRequestDtoIn | null>(null);

  // Build query params for server-side pagination, filtering, and sorting
  // Uses serverSort (not sorting) - serverSort only changes when server-side columns are selected
  const queryParams: SupportRequestsQueryParams = useMemo(
    () => ({
      sortBy: SORT_FIELD_MAP[serverSort.id] || 'RequestedAt',
      sortDirection: serverSort.desc ? 'Desc' : 'Asc',
      searchTerm: debouncedSearch ? translateGoalSearchTerm(debouncedSearch) : undefined,
      selectedTypes: selectedTypes.length > 0 ? selectedTypes : undefined,
      dateFrom: dateRange?.from || undefined,
      dateTo: dateRange?.to || undefined,
      fundId: selectedFundId ?? undefined,
      beneficiaryContactId: selectedBeneficiaryContactId ?? undefined,
      supplierId: selectedSupplierId ?? undefined,
      isThirdPartyPayment: isThirdPartyPaymentFilter,
    }),
    [serverSort, debouncedSearch, selectedTypes, dateRange, selectedFundId, selectedBeneficiaryContactId, selectedSupplierId, isThirdPartyPaymentFilter]
  );

  const {
    data: response,
    isLoading,
    isFetching,
    refetch: refetchSupportRequests,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = usePostAuthApiInfiniteQuery<ManagementSupportRequestDtoIn, SupportRequestsQueryParams>(
    `management/support-requests?tab=${selectedTab}`,
    queryParams,
    pageSize
  );

  const isTableBusy = isLoading || (isFetching && !isFetchingNextPage);

  // Refetch data when page size changes
  useEffect(() => {
    refetchSupportRequests();
  }, [pageSize, refetchSupportRequests]);

  const accumulatedData = useMemo(() => response?.pages.flatMap((page) => page.data) ?? [], [response]);

  // Eligible amounts state and mutation
  const [eligibleAmounts, setEligibleAmounts] = useState<Record<string, number>>({});
  const [isLoadingEligible, setIsLoadingEligible] = useState(false);
  // Track IDs that have already been calculated to avoid re-fetching
  const [calculatedIds, setCalculatedIds] = useState<Set<string>>(new Set());

  const { mutateAsync: fetchEligibleAmounts } = usePostAuthApi<EligibleAmountItemDtoIn[], EligibleAmountsQueryDtoIn>(
    `management/support-requests/eligible-amounts?tab=${selectedTab}`
  );

  // Reset calculated IDs and eligible amounts when tab or filters change
  // Also set isLoadingEligible to true so skeleton shows immediately
  useEffect(() => {
    setCalculatedIds(new Set());
    setEligibleAmounts({});
    setIsLoadingEligible(true);
  }, [selectedTab, queryParams]);

  // Fetch eligible amounts only for new items when accumulated data changes
  useEffect(() => {
    const fetchEligible = async () => {
      // If main query is still fetching, wait for it to complete
      // Keep isLoadingEligible as true (set by the queryParams useEffect)
      if (isFetching) {
        return;
      }

      if (accumulatedData.length === 0) {
        setIsLoadingEligible(false);
        return;
      }

      // Filter to only include items not yet calculated
      const newItems = accumulatedData
        .filter((r) => {
          const key =
            r.multiRequestCounter !== null && r.multiRequestCounter !== undefined
              ? `${r.multiRequestCorrelationId}_${r.multiRequestCounter}`
              : r.multiRequestCorrelationId;
          return !calculatedIds.has(key);
        })
        .map((r) => ({
          correlationId: r.multiRequestCorrelationId,
          counter: r.multiRequestCounter ?? null,
        }));

      // Skip if no new items to calculate
      if (newItems.length === 0) {
        setIsLoadingEligible(false);
        return;
      }
      try {
        const eligibleAmountsData = await fetchEligibleAmounts({ items: newItems });

        // Build map for the new items
        const newMap: Record<string, number> = {};
        const allRequestedKeys: string[] = [];
        
        // Track all requested items (even if backend didn't return a result)
        newItems.forEach((item) => {
          const key =
            item.counter !== null
              ? `${item.correlationId}_${item.counter}`
              : item.correlationId;
          allRequestedKeys.push(key);
        });

        // Add the results we got from backend
        eligibleAmountsData.forEach((item) => {
          const key =
            item.counter !== null
              ? `${item.correlationId}_${item.counter}`
              : item.correlationId;
          newMap[key] = item.eligibleAmount;
        });

        // Merge new amounts with existing ones
        setEligibleAmounts((prev) => ({ ...prev, ...newMap }));
        // Track ALL requested IDs as calculated (even if no result was returned)
        // This is a safety net: normally the backend should return a result for every request,
        // but if it doesn't (due to network error, timeout, or edge case), this prevents infinite loops
        setCalculatedIds((prev) => {
          const updated = new Set(prev);
          allRequestedKeys.forEach((key) => updated.add(key));
          return updated;
        });
      } catch (error) {
        console.error('Error fetching eligible amounts:', error);
      } finally {
        setIsLoadingEligible(false);
      }
    };

    fetchEligible();
  }, [accumulatedData, fetchEligibleAmounts, calculatedIds, isFetching]);

  // Combined refetch for support requests (eligible amounts will be fetched automatically via useEffect)
  const refetch = useCallback(() => {
    refetchSupportRequests();
  }, [refetchSupportRequests]);

  // Refresh balances cache hook
  const { mutate: refreshBalancesMutation, isPending: isRefreshingBalances } = useDeleteAuthApi<void>(
    'management/support-requests/balances-cache/purge'
  );

  // Keep selectedRequest in sync with accumulatedData after refetch.
  // If the request is no longer in the current tab's data (e.g. status changed), close the blade.
  // Waits for the fetch to settle rather than for a non-empty list: approving the last row of a
  // tab leaves it empty, and the old `length > 0` guard then kept the blade open on the
  // pre-approval request, its approve button live a second time (HAT-1391).
  useEffect(() => {
    if (selectedRequest && !isFetching) {
      const updatedRequest = accumulatedData.find(
        (r) =>
          r.multiRequestCorrelationId === selectedRequest.multiRequestCorrelationId &&
          r.multiRequestCounter === selectedRequest.multiRequestCounter
      );
      if (updatedRequest) {
        setSelectedRequest(updatedRequest);
        deepLinkActive.current = false;
      } else if (!deepLinkActive.current) {
        setSelectedRequest(undefined);
        setDocuments([]);
        // Close dialogs that depend on selectedRequest. Otherwise, when the user
        // later clicks on a different row, the dialog's open flag would still be true
        // and the dialog would pop up automatically for the newly-selected request.
        // Note: the goal-type and convert-to-fund-expense dialogs snapshot their own
        // request (see requestForGoalTypeChange / requestForConvertToFundExpense) so
        // they keep showing their post-success message even after the row migrates to
        // a different tab; they only close when the user explicitly closes them.
        setOfficeCommentsDialogOpen(false);
        setEditRequestedForDateDialogOpen(false);
        setRequestCompletionDialogOpen(false);
      }
    }
  }, [accumulatedData, isFetching]);

  const handleRefreshBalances = useCallback(() => {
    refreshBalancesMutation(undefined, {
      onSuccess: () => {
        // Eligible amounts are fetched once per row (see calculatedIds); forget them so
        // the refreshed data is re-calculated too, not just the balance columns (WOR-1392).
        setCalculatedIds(new Set());
        refetch();
      },
      onError: (error) => {
        console.error('Error refreshing balances:', error);
      }
    });
  }, [refreshBalancesMutation, refetch]);

  const handleStatusChange = (
    correlationId: string,
    counter: number | null,
    isInInitialStatusProcess: boolean,
    newStatus: string,
    // The blade passes its own request (HAT-1391): one opened from a deep link may not be in the
    // loaded rows yet, and without it the supplier approval gate would silently be skipped.
    knownRequest?: ManagementSupportRequestDtoIn
  ) => {
    const findRequest = () =>
      accumulatedData.find((r) =>
        r.multiRequestCorrelationId === correlationId &&
        (counter === null || r.multiRequestCounter === counter)
      ) ?? knownRequest;

    // If changing to PendingCompletion or DonePendingCompletion, open the request completion dialog
    if (newStatus === 'PendingCompletion' || newStatus === 'DonePendingCompletion') {
      const request = findRequest();
      if (request) {
        setSelectedRequest(request);
        setIsDoneWithPendingCompletion(newStatus === 'DonePendingCompletion');
        fetchDocuments(correlationId, counter);
        setRequestCompletionDialogOpen(true);
      }
      return;
    }

    // Open regular status change dialog
    const pendingRequest = findRequest() ?? null;
    setPendingStatusChange({
      correlationId,
      counter,
      isInInitialStatusProcess,
      newStatus,
      request: pendingRequest,
    });
    setStatusChangeDialogOpen(true);
  };

  // HAT-1391: once a request's status has been changed, the blade must not go on offering moves
  // computed from its old status. The server accepts Approved -> Approved and re-applies it to
  // every month, paid ones included, so a second click on a stale "approve" is a double payment
  // risk. Close the blade instead: reopening the row reads the request afresh.
  const closeBladeIfShowing = (changed: { correlationId: string; counter: number | null }[]) => {
    if (!selectedRequest) return;
    const isShown = changed.some(
      (c) =>
        c.correlationId === selectedRequest.multiRequestCorrelationId &&
        (c.counter ?? null) === (selectedRequest.multiRequestCounter ?? null)
    );
    if (!isShown) return;
    deepLinkActive.current = false;
    setSelectedRequest(undefined);
    setDocuments([]);
  };

  const handleStatusChangeSuccess = () => {
    if (pendingStatusChange) {
      closeBladeIfShowing([{ correlationId: pendingStatusChange.correlationId, counter: pendingStatusChange.counter }]);
    }
    refetch();
    queryClient.invalidateQueries({ predicate: (query) => queryKeys.fundBalance.isBalanceQuery(query.queryKey) });
    setStatusChangeDialogOpen(false);
    setPendingStatusChange(null);
  };

  const handleStatusChangeClose = () => {
    setStatusChangeDialogOpen(false);
    setPendingStatusChange(null);
  };

  const fetchDocuments = useCallback(async (correlationId: string, counter: number | null) => {
    if (!currentUser?.token) return;

    const headers = { Authorization: `Bearer ${currentUser.token}` };
    const counterParam = counter !== null ? `?counter=${counter}` : '';
    const url = `management/support-requests/${correlationId}/documents${counterParam}`;

    try {
      setIsLoadingDocs(true);
      const docs = await getAuth<DocumentWithDetailsDtoIn[]>(url, headers);
      setDocuments(docs);
    } catch (error) {
      console.error('Error fetching documents:', error);
      setDocuments([]);
    } finally {
      setIsLoadingDocs(false);
    }
  }, [currentUser?.token]);

  const deepLinkHandled = useRef(false);
  const deepLinkActive = useRef(false);
  useEffect(() => {
    if (deepLinkHandled.current) return;
    const params = new URLSearchParams(window.location.search);
    const resourceId = params.get('resourceId');
    if (!resourceId || !currentUser?.token) return;
    deepLinkHandled.current = true;
    deepLinkActive.current = true;
    const headers = { Authorization: `Bearer ${currentUser.token}` };
    getAuth<ManagementSupportRequestDtoIn>(`management/support-requests/${resourceId}/single`, headers)
      .then(item => {
        if (item) {
          setSelectedTab(getTabForRequest(item));
          setSelectedRequest(item);
          fetchDocuments(item.multiRequestCorrelationId, item.multiRequestCounter ?? null);
        }
      })
      .catch(() => {})
      .finally(() => window.history.replaceState({}, '', window.location.pathname));
  }, [currentUser?.token, fetchDocuments]);

  const handleRowClick = useCallback((row: ManagementSupportRequestDtoIn) => {
    setSelectedRequest(row);
    setDocuments([]);
    fetchDocuments(row.multiRequestCorrelationId, row.multiRequestCounter ?? null);
  }, [fetchDocuments]);

  const handleCloseBlade = useCallback(() => {
    setSelectedRequest(undefined);
    setDocuments([]);
  }, []);

  const handleOpenApprovedAmountDialog = (request: ManagementSupportRequestDtoIn) => {
    setRequestForAmountEdit(request);
    setApprovedAmountDialogOpen(true);
  };

  const handleCloseApprovedAmountDialog = () => {
    setApprovedAmountDialogOpen(false);
    setRequestForAmountEdit(null);
  };

  const handleApprovedAmountSuccess = () => {
    refetch();
  };

  const handleOpenChangeBankAccountDialog = useCallback(() => {
    if (selectedRequest) {
      setRequestForBankAccountEdit(selectedRequest);
      setChangeBankAccountDialogOpen(true);
    }
  }, [selectedRequest]);

  const handleCloseChangeBankAccountDialog = () => {
    setChangeBankAccountDialogOpen(false);
    setRequestForBankAccountEdit(null);
  };

  const handleChangeBankAccountSuccess = () => {
    refetch();
  };

  // CSV Export handler for selected rows
  const handleExportCsvForApt = useCallback((selectedRows: ManagementSupportRequestDtoIn[]) => {
    exportSupportRequestsToCsv(selectedRows);
  }, []);

  // Receipt download handler - only enabled (see selectionActions below) when every selected row
  // is a one-time request that actually reached Done, so no eligibility re-check is needed here.
  const [isDownloadingReceipts, setIsDownloadingReceipts] = useState(false);
  const handleDownloadReceipts = useCallback(async (selectedRows: ManagementSupportRequestDtoIn[]) => {
    setIsDownloadingReceipts(true);
    try {
      for (const row of selectedRows) {
        const counter = row.multiRequestCounter ?? 1;
        await downloadFileFromApi(
          `management/support-requests/${row.multiRequestCorrelationId}/${counter}/receipt`,
          `אסמכתא-${getShortRequestId(row.multiRequestCorrelationId)}.pdf`,
          currentUser?.token ?? ''
        );
      }
    } finally {
      setIsDownloadingReceipts(false);
    }
  }, [currentUser]);

  // Bulk status change handlers
  const handleOpenBulkStatusDialog = useCallback((selectedRows: ManagementSupportRequestDtoIn[]) => {
    const statuses = getCommonAllowedStatuses(selectedRows);
    setSelectedForBulkUpdate(selectedRows);
    setCommonStatuses(statuses);
    setBulkStatusDialogOpen(true);
  }, []);

  const handleCloseBulkStatusDialog = useCallback(() => {
    setBulkStatusDialogOpen(false);
    setSelectedForBulkUpdate([]);
    setCommonStatuses([]);
  }, []);

  const handleBulkStatusSuccess = useCallback(() => {
    closeBladeIfShowing(
      selectedForBulkUpdate.map((r) => ({ correlationId: r.multiRequestCorrelationId, counter: r.multiRequestCounter ?? null }))
    );
    refetch();
    queryClient.invalidateQueries({ predicate: (query) => queryKeys.fundBalance.isBalanceQuery(query.queryKey) });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- closeBladeIfShowing only reads selectedRequest, listed here
  }, [refetch, selectedForBulkUpdate, selectedRequest]);

  // Office comments handlers
  const handleOpenOfficeCommentsDialog = useCallback(() => {
    setOfficeCommentsDialogOpen(true);
  }, []);

  const handleCloseOfficeCommentsDialog = useCallback(() => {
    setOfficeCommentsDialogOpen(false);
  }, []);

  const handleOfficeCommentsSuccess = useCallback(() => {
    refetch();
  }, [refetch]);

  // Edit requested-for-date handlers
  const handleOpenEditRequestedForDateDialog = useCallback(() => {
    setEditRequestedForDateDialogOpen(true);
  }, []);

  const handleCloseEditRequestedForDateDialog = useCallback(() => {
    setEditRequestedForDateDialogOpen(false);
  }, []);

  const handleEditRequestedForDateSuccess = useCallback(() => {
    refetch();
  }, [refetch]);

  // Request completion handlers
  const handleOpenRequestCompletionDialog = useCallback(() => {
    setRequestCompletionDialogOpen(true);
  }, []);

  const handleCloseRequestCompletionDialog = useCallback(() => {
    setRequestCompletionDialogOpen(false);
    setIsDoneWithPendingCompletion(false);
  }, []);

  const handleRequestCompletionSuccess = useCallback(() => {
    queryClient.refetchQueries({
      predicate: (query) => queryKeys.supportRequests.isManagementQuery(query.queryKey),
    });
    queryClient.invalidateQueries({ predicate: (query) => queryKeys.fundBalance.isBalanceQuery(query.queryKey) });
    if (selectedRequest) {
      fetchDocuments(selectedRequest.multiRequestCorrelationId, selectedRequest.multiRequestCounter ?? null);
    }
  }, [selectedRequest, fetchDocuments]);

  // Documents only: no status moved, so the table and balances need no refetch.
  const handleDocumentsUploaded = useCallback(() => {
    if (selectedRequest) {
      fetchDocuments(selectedRequest.multiRequestCorrelationId, selectedRequest.multiRequestCounter ?? null);
    }
  }, [selectedRequest, fetchDocuments]);

  const handleOpenChangeGoalTypeDialog = useCallback(() => {
    if (selectedRequest) {
      setRequestForGoalTypeChange(selectedRequest);
    }
  }, [selectedRequest]);

  const handleCloseChangeGoalTypeDialog = useCallback(() => {
    setRequestForGoalTypeChange(null);
  }, []);

  // Refetch all management tabs (not just the current one): changing a goal can move
  // the request between tabs (e.g. to the exceptions committee), so switching to the
  // destination tab must show fresh data instead of a stale cached list.
  const handleChangeGoalTypeSuccess = useCallback(() => {
    queryClient.refetchQueries({
      predicate: (query) => queryKeys.supportRequests.isManagementQuery(query.queryKey),
    });
  }, []);

  const handleOpenConvertToFundExpenseDialog = useCallback(() => {
    if (selectedRequest) {
      setRequestForConvertToFundExpense(selectedRequest);
    }
  }, [selectedRequest]);

  const handleCloseConvertToFundExpenseDialog = useCallback(() => {
    setRequestForConvertToFundExpense(null);
  }, []);

  // Converting a supported-beneficiary request to a fund-expense request also changes
  // the request type and may move it to a different management tab, so refetch all tabs.
  const handleConvertToFundExpenseSuccess = useCallback(() => {
    queryClient.refetchQueries({
      predicate: (query) => queryKeys.supportRequests.isManagementQuery(query.queryKey),
    });
  }, []);

  // Sorting change handler
  const handleSortingChange = useCallback((newSorting: SortingState) => {
    setSorting(newSorting);
    // Update serverSort only when a server-side column is selected
    // This keeps the last server sort as a "tiebreaker" for client-side sorts
    const newSortId = newSorting[0]?.id;
    if (newSortId && SORT_FIELD_MAP[newSortId]) {
      setServerSort({ id: newSortId, desc: newSorting[0]?.desc ?? false });
    }
  }, []);

  // In first three tabs (PendingApproval, PendingExceptionsCommitteeApproval, CompletionReturned) show submission date,
  // in other tabs show requested-for date (since each row has a single requested-for date)
  const showSubmissionDate = selectedTab <= ManagementTabType.CompletionReturned;

  const columns: ColumnDef<ManagementSupportRequestDtoIn>[] = [
    // Selection column
    {
      id: 'select',
      header: ({ table }) => <SelectAllHeader table={table} disabled={isTableBusy} />,
      cell: Cell(cellType.Selection),
      meta: {
        className: 'max-w-[48px] min-w-[48px] w-[48px]',
        lockLayout: true,
      },
    },
    {
      id: 'dateColumn',
      header: Col(t(showSubmissionDate ? 'submissionDate' : 'supportDate')),
      cell: Cell(cellType.Date),
      accessorFn: (row) => showSubmissionDate ? row.requestedAt : row.requestedForDates[0],
      meta: {
        className: 'max-w-[127px] min-w-[127px] w-[127px]',
      },
    },
    {
      id: 'fundId',
      header: Col(t('fund')),
      cell: Cell(cellType.Number),
      accessorKey: 'fundId',
      meta: {
        className: 'max-w-[80px] min-w-[80px] w-[80px]',
      },
    },
    {
      id: 'supportedName',
      header: Col(t('recipientName')),
      cell: ({ row }) => {
        const isFundExpense = row.original.goal?.allowedFor === AllowedFor.Fund;
        const details = row.original.additionalDetails as FundExpenseDetails | undefined;
        const supplierDisplay = getSupplierDisplayDetails(row.original.supplier, row.original.additionalDetails);

        if (isFundExpense) {
          const name = details?.isAlreadyPaid ? details?.payerName : supplierDisplay?.name;
          const tooltipText = details?.isAlreadyPaid ? t('payer') : t('supplier');
          const tooltipId = `recipient-info-${row.id}`;
          return (
            <div className="flex items-center gap-1.5">
              <img id={tooltipId} src={infoIcon} alt="" className="w-4 h-4" />
              <CustomTooltip targetId={tooltipId}>
                <span className="body-xs-sm-regular">{tooltipText}</span>
              </CustomTooltip>
              <span className={tableStyles.cellText} style={{ fontWeight: 600 }}>{name ?? ''}</span>
            </div>
          );
        }

        const { firstName, lastName, id: supportedId } = row.original.beneficiaryContact ?? {};
        const fundId = row.original.fundId;
        return (
          <div className="flex items-center gap-1.5">
            <a
              className={`text-(--font-link) cursor-pointer ${tableStyles.cellText}`}
              style={{ fontWeight: 600, textDecoration: 'underline' }}
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/${fundId}/support/supporteds`, { state: { supportedId } });
              }}
            >
              {firstName} {lastName}
            </a>
            {/* HAT-1259: the row still names the beneficiary, because the request is theirs and
                counts against their quota - the tag is what says the money goes elsewhere. */}
            {row.original.isThirdPartyPayment && (
              <span
                className="shrink-0 px-1.5 py-0.5 rounded body-xs-sm-regular bg-(--surface-secondary-lowest) border border-(--border-main)"
                title={t('thirdPartyPayment.tagTooltip', {
                  payee: row.original.thirdPartyPayee?.name ?? '',
                })}
              >
                {t('thirdPartyPayment.tag')}
              </span>
            )}
          </div>
        );
      },
      accessorFn: (row) => {
        if (row.goal?.allowedFor === AllowedFor.Fund) {
          const details = row.additionalDetails as FundExpenseDetails | undefined;
          const supplierDisplay = getSupplierDisplayDetails(row.supplier, row.additionalDetails);
          const name = details?.isAlreadyPaid ? details?.payerName : supplierDisplay?.name;
          return { firstName: '', lastName: name ?? '' };
        }
        return {
          firstName: row.beneficiaryContact?.firstName ?? '',
          lastName: row.beneficiaryContact?.lastName ?? '',
        };
      },
      meta: {
        className: 'max-w-[150px] min-w-[150px] w-[150px]',
      },
    },
    {
      id: 'supportedIsraeliId',
      header: Col(t('israeliIdOrCompanyNumber')),
      cell: Cell(cellType.String),
      enableSorting: false,
      accessorFn: (row) => {
        if (row.goal?.allowedFor === AllowedFor.Fund) {
          const details = row.additionalDetails as FundExpenseDetails | undefined;
          const supplierDisplay = getSupplierDisplayDetails(row.supplier, row.additionalDetails);
          return details?.isAlreadyPaid ? (details?.payerIsraeliId ?? '') : (supplierDisplay?.taxId ?? '');
        }
        return row.beneficiaryContact?.israeliId ?? '';
      },
      meta: {
        className: 'max-w-[100px] min-w-[100px] w-[100px]',
      },
    },
    {
      id: 'requestedAmount',
      header: Col(t('requestedAmount')),
      cell: Cell(cellType.Amount),
      accessorFn: (row) => ({ amount: row.requestedAmount, currency: 'ILS' }),
      meta: {
        className: 'max-w-[100px] min-w-[100px] w-[100px]',
      },
    },
    {
      id: 'approvedAmount',
      header: Col(t('approvedAmount')),
      cell: ({ row }) => {
        const amount = row.original.approvedAmount;
        const formattedAmount = amount.toLocaleString('he-IL', { style: 'currency', currency: 'ILS' });
        const canEditAmount = selectedTab <= ManagementTabType.Approved;
        return (
          <div className="flex items-center gap-2">
            <span>{formattedAmount}</span>
            {canEditAmount && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  if (isTableBusy) return;
                  handleOpenApprovedAmountDialog(row.original);
                }}
                disabled={isTableBusy}
                className="p-1 cursor-pointer hover:opacity-70 disabled:opacity-40 disabled:cursor-not-allowed min-w-[28px] min-h-[28px]"
                title={t('changeApprovedAmount')}
                aria-busy={isTableBusy || undefined}
              >
                <img src={editIcon} alt="" />
              </button>
            )}
          </div>
        );
      },
      meta: {
        className: 'max-w-[100px] min-w-[100px] w-[100px]',
      },
    },
    {
      id: 'fundBalance',
      header: Col(t('fundBalance')),
      cell: Cell(cellType.Amount),
      // Whole shekels: with agorot a seven-figure balance in bold was clipped at its start (-424,918.66 read 24,918.66).
      accessorFn: (row) => ({ amount: row.fundBalance ?? 0, currency: 'ILS', fractionDigits: 0 }),
      sortingFn: (a, b) => (a.original.fundBalance ?? 0) - (b.original.fundBalance ?? 0),
      meta: {
        // -8,245,550 ₪ in bold is about 88px; 100px left 76 of it after the padding.
        className: 'max-w-[110px] min-w-[110px] w-[110px]',
      },
    },
    {
      id: 'fundAvailableBalance',
      header: Col(t('fundAvailableBalance')),
      cell: ({ row }) => {
        const amount = row.original.fundAvailableBalance ?? 0;
        const formatAmount = (n: number) =>
          n.toLocaleString(i18n.language, {
            style: 'currency',
            currency: 'ILS',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
          });
        const tooltipText = buildAvailableBalancePendingTooltipText(t, formatAmount, row.original);
        const tooltipId = `available-balance-mgmt-${row.id}`;
        return (
          <div dir="rtl" className="flex items-center gap-1">
            {/* Whole shekels, like the tooltip: with agorot a seven-figure balance left no room for the «?». */}
            <span className={`whitespace-nowrap ${amount < 0 ? 'text-(--font-primary)' : 'text-(--font-tertiary)'}`}>
              {formatAmount(amount)}
            </span>
            <img id={tooltipId} src={tooltipQuestion} alt="" width={16} className="shrink-0" />
            <CustomTooltip targetId={tooltipId}>
              <p className="body-sm-regular text-(--font-sm-title)" style={{ whiteSpace: 'pre-line' }}>
                {tooltipText}
              </p>
            </CustomTooltip>
          </div>
        );
      },
      accessorFn: (row) => ({ amount: row.fundAvailableBalance ?? 0, currency: 'ILS' }),
      sortingFn: (a, b) => (a.original.fundAvailableBalance ?? 0) - (b.original.fundAvailableBalance ?? 0),
      meta: {
        // The amount plus its «?»: at 100px a seven-figure balance pushed the icon out of the clipped cell.
        className: 'max-w-[120px] min-w-[120px] w-[120px]',
      },
    },
    {
      id: 'eligibleAmount',
      header: Col(t('eligibleAmount')),
      enableSorting: !isLoadingEligible,
      accessorFn: (row) => {
        const key =
          row.multiRequestCounter !== null && row.multiRequestCounter !== undefined
            ? `${row.multiRequestCorrelationId}_${row.multiRequestCounter}`
            : row.multiRequestCorrelationId;
        return eligibleAmounts[key] ?? 0;
      },
      cell: ({ row }) => {
        const key =
          row.original.multiRequestCounter !== null && row.original.multiRequestCounter !== undefined
            ? `${row.original.multiRequestCorrelationId}_${row.original.multiRequestCounter}`
            : row.original.multiRequestCorrelationId;

        if (isLoadingEligible) {
          return <Skeleton variant="text" width={60} height={16} animation="pulse" />;
        }

        const amount = eligibleAmounts[key];
        if (amount === undefined || amount === Number.MAX_VALUE) {
          return <div>--</div>;
        }
        return <div>{amount.toLocaleString('he-IL', { style: 'currency', currency: 'ILS' })}</div>;
      },
      meta: {
        className: 'max-w-[100px] min-w-[100px] w-[100px]',
      },
    },
    {
      id: 'goalName',
      header: Col(t('goal')),
      cell: ({ getValue }) => {
        const goalName = getValue() as string;
        return <div>{t(goalName)}</div>;
      },
      accessorFn: (row) => row.goal?.name ?? '',
      meta: {
        className: 'max-w-[100px] min-w-[100px] w-[100px]',
      },
    },
    {
      id: 'counter',
      header: Col(t('counter')),
      enableSorting: false,
      cell: ({ row }) => {
        const { type, isInInitialStatusProcess, multiRequestTotalCount, multiRequestCounter } = row.original;
        if (type === SupportRequestType.OneTime) {
          return <div>חד"פ</div>;
        }
        // Monthly
        if (isInInitialStatusProcess) {
          return <div>{multiRequestTotalCount} חודשים</div>;
        }
        return <div>{multiRequestCounter}/{multiRequestTotalCount}</div>;
      },
      meta: {
        className: 'max-w-[80px] min-w-[80px] w-[80px]',
      },
    },
    {
      id: 'combinedStatus',
      header: Col(t('status')),
      enableSorting: false,
      cell: ({ row }) => {
        const { isInInitialStatusProcess, initialStatus, status, allowedNextStatuses, multiRequestCorrelationId, multiRequestCounter } =
          row.original;
        const currentStatus = isInInitialStatusProcess ? String(initialStatus) : String(status);
        // HAT-1395: a month that was approved once and is back for its new account waits for that
        // approval, not for a "first" one.
        const labelOf = (s: string) => (!isInInitialStatusProcess && s === 'PendingInitialApproval' ? t('returnedPayment.officePendingLabel') : t(s));

        // If no allowed transitions, show as text only
        const statusLabel = labelOf(currentStatus);

        /* HAT-1395: what the status alone does not say, as a mark beside it. Not beside the name,
           where a tag read as if the payee had changed; and not under the status, where a second
           line ran into the next row - the rows here have a fixed height. Every open month whose
           account was deleted waits for the coordinator - a month in «ממתינה לביצוע» above all,
           which would otherwise be paid to the wrong account; an approved month back in
           «ממתין לאישור» is there for its new account. */
        const waitsForAccount = !!row.original.paymentInfoDeletedForReturnedPayment && !CLOSED_STATUSES.includes(currentStatus);
        const canAskForNewAccount =
          isReturnedPaymentOn &&
          !isInInitialStatusProcess &&
          String(status) === 'PaymentReturned' &&
          !row.original.paymentInfoDeletedForReturnedPayment &&
          row.original.paymentMethodType === PaymentMethodType.BankAccount &&
          !row.original.isThirdPartyPayment;
        const returnedPaymentMark =
          waitsForAccount
            ? {
                label: t('returnedPayment.officeWaitingTag'),
                tooltip: currentStatus === 'PendingExecution' ? t('returnedPayment.officeDoNotExecuteTooltip') : t('returnedPayment.officeWaitingTagTooltip'),
                className: 'bg-(--surface-warning-lowest) border-(--border-warning)',
              }
            : !isInInitialStatusProcess && String(status) === 'PendingInitialApproval'
              ? { label: t('returnedPayment.officeNewAccountTag'), tooltip: t('returnedPayment.officeNewAccountTagTooltip'), className: 'bg-(--surface-secondary-lowest) border-(--border-main)' }
              : null;
        const withTag = (content: ReactNode) =>
          returnedPaymentMark ? (
            <div className="flex items-center gap-1.5 min-w-0">
              <span
                className={`shrink-0 inline-flex items-center justify-center w-6 h-6 rounded-full border ${returnedPaymentMark.className}`}
                title={`${returnedPaymentMark.label} – ${returnedPaymentMark.tooltip}`}
                aria-label={returnedPaymentMark.label}
                role="img"
              >
                <img src={bankIcon} alt="" className="w-3.5 h-3.5" />
              </span>
              <div className="min-w-0 flex-1">{content}</div>
            </div>
          ) : (
            content
          );

        if (!allowedNextStatuses || allowedNextStatuses.length === 0) {
          return withTag(<div className={tableStyles.cellText} title={statusLabel}>{statusLabel}</div>);
        }

        return withTag(
          <select
            value={currentStatus}
            title={statusLabel}
            disabled={isTableBusy}
            onChange={(e) =>
              handleStatusChange(
                multiRequestCorrelationId,
                multiRequestCounter ?? null,
                isInInitialStatusProcess,
                e.target.value === ASK_NEW_ACCOUNT ? 'PaymentReturned' : e.target.value
              )
            }
            className={tableStyles.statusSelect}
            onClick={(e) => e.stopPropagation()}
            aria-busy={isTableBusy || undefined}
          >
            <option value={currentStatus}>{statusLabel}</option>
            {canAskForNewAccount && <option value={ASK_NEW_ACCOUNT}>{t('returnedPayment.askNewAccountOption')}</option>}
            {allowedNextStatuses
              .filter((nextStatus) => nextStatus !== currentStatus)
              .map((nextStatus) => (
                <option key={nextStatus} value={nextStatus}>
                  {labelOf(nextStatus)}
                </option>
              ))}
          </select>
        );
      },
      meta: {
        className: 'max-w-[180px] min-w-[160px] w-[180px]',
      },
    },
  ];

  // Export columns - return plain values (not JSX) for Excel export
  const exportColumns: ColumnDef<ManagementSupportRequestDtoIn>[] = [
    {
      id: 'dateColumn',
      header: showSubmissionDate ? 'submissionDate' : 'supportDate',
      accessorFn: (row) => showSubmissionDate ? row.requestedAt : row.requestedForDates[0],
    },
    {
      id: 'fundId',
      header: 'fund',
      accessorKey: 'fundId',
    },
    {
      id: 'supportedName',
      header: 'recipientName',
      accessorFn: (row) => {
        if (row.goal?.allowedFor === AllowedFor.Fund) {
          const details = row.additionalDetails as FundExpenseDetails | undefined;
          const supplierDisplay = getSupplierDisplayDetails(row.supplier, row.additionalDetails);
          return details?.isAlreadyPaid ? (details?.payerName ?? '') : (supplierDisplay?.name ?? '');
        }
        return row.beneficiaryContact ? `${row.beneficiaryContact.firstName} ${row.beneficiaryContact.lastName}` : '';
      },
    },
    {
      id: 'supportedIsraeliId',
      header: 'israeliIdOrCompanyNumber',
      accessorFn: (row) => {
        if (row.goal?.allowedFor === AllowedFor.Fund) {
          const details = row.additionalDetails as FundExpenseDetails | undefined;
          const supplierDisplay = getSupplierDisplayDetails(row.supplier, row.additionalDetails);
          return details?.isAlreadyPaid ? (details?.payerIsraeliId ?? '') : (supplierDisplay?.taxId ?? '');
        }
        return row.beneficiaryContact?.israeliId ?? '';
      },
    },
    {
      id: 'supportedContactId',
      header: 'contactId',
      accessorFn: (row) => row.beneficiaryContact?.id ?? '',
    },
    // HAT-1259. Three columns rather than one flag: the office's whole complaint was that it
    // could not see who was actually paid, so the export has to carry the payee, not just a yes.
    {
      id: 'isThirdPartyPayment',
      header: 'thirdPartyPayment.exportIsThirdParty',
      accessorFn: (row) => (row.isThirdPartyPayment ? t('yes') : t('no')),
    },
    {
      id: 'thirdPartyPayeeName',
      header: 'thirdPartyPayment.exportPayeeName',
      accessorFn: (row) => row.thirdPartyPayee?.name ?? '',
    },
    {
      id: 'thirdPartyReason',
      header: 'thirdPartyPayment.exportReason',
      accessorFn: (row) => (row.thirdPartyReason
        ? t(`thirdPartyPayment.reason.${row.thirdPartyReason}`)
        : ''),
    },
    {
      id: 'invoiceNumber',
      header: 'invoiceNumber',
      accessorFn: (row) => row.invoiceNumber ?? '',
    },
    {
      id: 'requestedAmount',
      header: 'requestedAmount',
      accessorFn: (row) => row.requestedAmount.toLocaleString('he-IL', { style: 'currency', currency: 'ILS' }),
    },
    {
      id: 'approvedAmount',
      header: 'approvedAmount',
      accessorFn: (row) => (row.approvedAmount ?? 0).toLocaleString('he-IL', { style: 'currency', currency: 'ILS' }),
    },
    {
      id: 'fundBalance',
      header: 'fundBalance',
      accessorFn: (row) => (row.fundBalance ?? 0).toLocaleString('he-IL', { style: 'currency', currency: 'ILS' }),
    },
    {
      id: 'fundAvailableBalance',
      header: 'fundAvailableBalance',
      accessorFn: (row) => (row.fundAvailableBalance ?? 0).toLocaleString('he-IL', { style: 'currency', currency: 'ILS' }),
    },
    {
      id: 'eligibleAmount',
      header: 'eligibleAmount',
      accessorFn: (row) => {
        const key =
          row.multiRequestCounter !== null && row.multiRequestCounter !== undefined
            ? `${row.multiRequestCorrelationId}_${row.multiRequestCounter}`
            : row.multiRequestCorrelationId;
        const amount = eligibleAmounts[key];
        if (amount === undefined || amount === Number.MAX_VALUE) {
          return '--';
        }
        return amount.toLocaleString('he-IL', { style: 'currency', currency: 'ILS' });
      },
    },
    {
      id: 'goalName',
      header: 'goal',
      accessorFn: (row) => t(row.goal?.name ?? ''),
    },
    {
      id: 'counter',
      header: 'counter',
      accessorFn: (row) => {
        if (row.type === SupportRequestType.OneTime) {
          return 'חד"פ';
        }
        if (row.isInInitialStatusProcess) {
          return `${row.multiRequestTotalCount} חודשים`;
        }
        return `${row.multiRequestCounter}/${row.multiRequestTotalCount}`;
      },
    },
    {
      id: 'status',
      header: 'status',
      accessorFn: (row) => {
        const currentStatus = row.isInInitialStatusProcess ? String(row.initialStatus) : String(row.status);
        return t(currentStatus);
      },
    },
  ];

  return (
    <div>
      {selectedRequest && (() => {
        // Statuses where reclassification (change goal / convert to fund expense) is still allowed.
        // The list mixes values from SupportRequestStatuses (used after initial approval) and
        // SupportRequestInitialStatusEnum (used while the request is still in the initial-approval
        // process). It is the inverse of the terminal statuses: Done / Rejected / Cancelled /
        // Approved-initial-status (which is never read here since isInInitialStatusProcess flips
        // to false once initial approval lands).
        const nonTerminalStatuses = new Set<string>([
          // SupportRequestStatuses (post initial approval)
          'PendingInitialApproval',
          'InitialApprovalApproved',
          'PendingExceptionsCommitteeApproval',
          'PendingExecution',
          'PendingCompletion',
          'CompletionReturned',
          'PaymentReturned',
          // SupportRequestInitialStatusEnum (while still in initial-approval process)
          'PendingFirstReview',
          'PendingManagementApproval',
        ]);
        const currentStatus = selectedRequest.isInInitialStatusProcess
          ? String(selectedRequest.initialStatus)
          : String(selectedRequest.status);
        const canReclassify = nonTerminalStatuses.has(currentStatus);
        const isFundExpense = selectedRequest.goal?.allowedFor === AllowedFor.Fund;

        return (
          <AdditionalInfo
            request={selectedRequest}
            documents={documents}
            isLoadingDocs={isLoadingDocs}
            handleXclick={handleCloseBlade}
            onChangeBankAccount={handleOpenChangeBankAccountDialog}
            onOpenOfficeComments={handleOpenOfficeCommentsDialog}
            onEditRequestedForDate={handleOpenEditRequestedForDateDialog}
            onRequestCompletion={
              selectedRequest.isInInitialStatusProcess || selectedRequest.type !== SupportRequestType.Monthly
                ? handleOpenRequestCompletionDialog
                : undefined
            }
            uploadDocumentsEndpoint={`management/support-requests/${selectedRequest.multiRequestCorrelationId}/documents${selectedRequest.multiRequestCounter != null ? `?counter=${selectedRequest.multiRequestCounter}` : ''}`}
            onDocumentsUploaded={handleDocumentsUploaded}
            onChangeGoalType={canReclassify && isFundExpense ? handleOpenChangeGoalTypeDialog : undefined}
            onConvertToFundExpense={canReclassify && !isFundExpense ? handleOpenConvertToFundExpenseDialog : undefined}
            relatedSupportRequests={relatedSupportRequests}
            isLoadingRelatedSupportRequests={isLoadingRelatedSupportRequests}
            onStatusChange={(newStatus) =>
              handleStatusChange(
                selectedRequest.multiRequestCorrelationId,
                selectedRequest.multiRequestCounter ?? null,
                selectedRequest.isInInitialStatusProcess,
                newStatus,
                selectedRequest,
              )
            }
            isStatusChangeDisabled={isTableBusy}
          />
        );
      })()}
      {selectedRequest && (
        <RequestCompletionDialog
          isOpen={requestCompletionDialogOpen}
          onClose={handleCloseRequestCompletionDialog}
          apiEndpoint={`management/support-requests/${selectedRequest.multiRequestCorrelationId}/request-completion${selectedRequest.multiRequestCounter != null ? `?counter=${selectedRequest.multiRequestCounter}` : ''}`}
          onSuccess={handleRequestCompletionSuccess}
          documents={documents}
          isDoneWithPendingCompletion={isDoneWithPendingCompletion}
          defaultFrozenAmount={selectedRequest.approvedAmount}
        />
      )}
      {selectedRequest && (
        <EditRequestedForDateDialog
          open={editRequestedForDateDialogOpen}
          onClose={handleCloseEditRequestedForDateDialog}
          request={selectedRequest}
          onSuccess={handleEditRequestedForDateSuccess}
        />
      )}
      {selectedRequest && (
        <OfficeCommentsDialog
          open={officeCommentsDialogOpen}
          onClose={handleCloseOfficeCommentsDialog}
          supportedName={
            selectedRequest.beneficiaryContact
              ? `${selectedRequest.beneficiaryContact.firstName} ${selectedRequest.beneficiaryContact.lastName}`
              : undefined
          }
          correlationId={selectedRequest.multiRequestCorrelationId}
          counter={selectedRequest.multiRequestCounter ?? null}
          totalCount={selectedRequest.multiRequestTotalCount}
          isInInitialStatusProcess={selectedRequest.isInInitialStatusProcess}
          officeComments={selectedRequest.officeComments || []}
          onSuccess={handleOfficeCommentsSuccess}
        />
      )}
      {requestForAmountEdit && (
        <ApprovedAmountDialog
          open={approvedAmountDialogOpen}
          onClose={handleCloseApprovedAmountDialog}
          request={requestForAmountEdit}
          onSuccess={handleApprovedAmountSuccess}
        />
      )}
      {pendingStatusChange && (
        <StatusChangeDialog
          open={statusChangeDialogOpen}
          onClose={handleStatusChangeClose}
          correlationId={pendingStatusChange.correlationId}
          counter={pendingStatusChange.counter}
          isInInitialStatusProcess={pendingStatusChange.isInInitialStatusProcess}
          newStatus={pendingStatusChange.newStatus}
          request={pendingStatusChange.request}
          onSuccess={handleStatusChangeSuccess}
        />
      )}
      {requestForBankAccountEdit && (
        <ChangeBankAccountDialog
          open={changeBankAccountDialogOpen}
          onClose={handleCloseChangeBankAccountDialog}
          request={requestForBankAccountEdit}
          onSuccess={handleChangeBankAccountSuccess}
        />
      )}
      {requestForGoalTypeChange && (
        <ChangeGoalTypeDialog
          open={!!requestForGoalTypeChange}
          onClose={handleCloseChangeGoalTypeDialog}
          request={requestForGoalTypeChange}
          onSuccess={handleChangeGoalTypeSuccess}
        />
      )}
      {requestForConvertToFundExpense && (
        <ConvertToFundExpenseDialog
          open={!!requestForConvertToFundExpense}
          onClose={handleCloseConvertToFundExpenseDialog}
          request={requestForConvertToFundExpense}
          onSuccess={handleConvertToFundExpenseSuccess}
        />
      )}
      <BulkStatusChangeDialog
        open={bulkStatusDialogOpen}
        onClose={handleCloseBulkStatusDialog}
        selectedRequests={selectedForBulkUpdate}
        commonStatuses={commonStatuses}
        onSuccess={handleBulkStatusSuccess}
      />
      <Tabs value={String(selectedTab)} onValueChange={(value) => setSelectedTab(Number(value) as ManagementTabType)} style={{ direction: 'rtl' }} className="mb-6">
        <TabsList variant="pills">
          <TabsTrigger variant="pills" value={String(ManagementTabType.PendingApproval)}>{t('PendingApproval')}</TabsTrigger>
          <TabsTrigger variant="pills" value={String(ManagementTabType.PendingExceptionsCommitteeApproval)}>
            {t('PendingExceptionsCommitteeApproval')}
          </TabsTrigger>
          <TabsTrigger variant="pills" value={String(ManagementTabType.CompletionReturned)}>{t('DocumentCompletion')}</TabsTrigger>
          <TabsTrigger variant="pills" value={String(ManagementTabType.Approved)}>{t('Approved')}</TabsTrigger>
          <TabsTrigger variant="pills" value={String(ManagementTabType.PendingExecution)}>{t('PendingExecution')}</TabsTrigger>
          <TabsTrigger variant="pills" value={String(ManagementTabType.Archive)}>{t('Archive')}</TabsTrigger>
        </TabsList>
      </Tabs>
      <DataTable
        title={t('supportRequests')}
        data={accumulatedData}
        getRowId={(row) => (row.multiRequestCounter != null ? `${row.multiRequestCorrelationId}_${row.multiRequestCounter}` : row.multiRequestCorrelationId)}
        isLoading={isTableBusy}
        columns={columns}
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        density={density}
        onDensityChange={setDensity}
        columnLayoutKey="supportRequestsManagement"
        searchPlaceholder="searchManagementRequest"
        searchTooltip="searchManagementRequestTooltip"
        tableHeight="calc(100vh - 380px)"
        filters={
          <FiltersArea
            selectedTypes={selectedTypes}
            setSelectedTypes={setSelectedTypes}
            dateRange={dateRange}
            setDateRange={setDateRange}
            pageSize={pageSize}
            onPageSizeChange={setPageSize}
            onRefreshBalances={handleRefreshBalances}
            isRefreshingBalances={isRefreshingBalances}
            selectedFundId={selectedFundId}
            onFundChange={setSelectedFundId}
            selectedBeneficiaryContactId={selectedBeneficiaryContactId}
            onBeneficiaryChange={setSelectedBeneficiaryContactId}
            selectedSupplierId={selectedSupplierId}
            onSupplierChange={setSelectedSupplierId}
            isThirdPartyPayment={isThirdPartyPaymentFilter}
            onThirdPartyPaymentChange={setIsThirdPartyPaymentFilter}
            isBusy={isTableBusy}
          />
        }
        customGlobalSearch={() => true}
        handleRowClick={handleRowClick}
        activeRecord={selectedRequest}
        exportColumns={exportColumns}
        exportData={accumulatedData}
        exportFilename={t('supportRequests')}
        onLoadMore={fetchNextPage}
        hasMore={hasNextPage}
        isLoadingMore={isFetchingNextPage}
        sortBy={sorting[0]}
        onSortingChange={handleSortingChange}
        selectionActions={[
          {
            label: t('exportCsvForApt'),
            onClick: handleExportCsvForApt,
          },
          {
            label: t('bulkSupportRequestStatus.changeStatus'),
            onClick: handleOpenBulkStatusDialog,
            isVisible: (selectedRows) => getCommonAllowedStatuses(selectedRows).length > 0,
          },
          {
            label: isDownloadingReceipts ? t('downloadingTransferReceipt') : t('downloadTransferReceipt'),
            onClick: handleDownloadReceipts,
            isVisible: (selectedRows) => selectedRows.length > 0 && selectedRows.every(isReceiptEligible),
            disabled: isDownloadingReceipts,
          },
        ]}
        selectionSummary={(selectedRows) => {
          const totalRequested = selectedRows.reduce((sum, row) => sum + row.requestedAmount, 0);
          const totalApproved = selectedRows.reduce((sum, row) => sum + row.approvedAmount, 0);
          const formatCurrency = (value: number) =>
            value.toLocaleString('he-IL', { style: 'currency', currency: 'ILS' });
          return (
            <>
              <span className="body-md-semibold text-(--font-body-4)">|</span>
              <span className="body-md-semibold text-(--font-body-4)">
                {t('totalRequestedAmount')}: {formatCurrency(totalRequested)}
              </span>
              <span className="body-md-semibold text-(--font-body-4)">|</span>
              <span className="body-md-semibold text-(--font-body-4)">
                {t('totalApprovedAmount')}: {formatCurrency(totalApproved)}
              </span>
            </>
          );
        }}
      />
    </div>
  );
};

export default SupportRequestsManagementPage;
